import Spinner from 'modules/loadingSpinner';
import { debounce } from 'lodash-es';
import { useDeleteAllMutation, useWrappingIndexQuery } from 'autogen/swagger/all/hooks';
import { useTranslation } from 'react-i18next';
import { Delete } from 'autogen/translation-keys/trans-website-shared';
import {
    ConfirmButton,
    Heading,
    Message
} from 'autogen/translation-keys/trans-website-basket-wrapping-deleteoreditconfirmation';
import dynamic from 'helpers/dynamic';
import { updateBasket } from 'components/basket/shared/basket';
import { GA4Tracking } from 'modules/tracking/GA4';

const WrappingView = dynamic(() => import('views/basket/wrapping/src/view'), { ssr: false });

function ConfirmModalContent(props: { message: string; additionalMessage?: string }): JSX.Element {
    const { message, additionalMessage } = props;

    return (
        <p className="mt-4 mb-5 text-center">
            {additionalMessage ? (
                <>
                    <p>{additionalMessage}</p>
                    <p>{message}</p>
                </>
            ) : (
                message
            )}
        </p>
    );
}

export default function useWrappingModalOpener(productId, isWrappingCardOnly) {
    const { t } = useTranslation();
    const { refetch: getWrappingModel } = useWrappingIndexQuery({ productId }, { enabled: false });
    const { mutateAsync: deleteAllWrapping } = useDeleteAllMutation({ onSuccess: updateBasket });

    const handleOpenWrappingModal = async () => {
        const { default: createModal } = await import('modules/modal');
        const { rebindBasket } = await import('views/basket');

        const modal = debounce(createModal(), 100);
        modal({ open: true, content: <Spinner /> });

        const { data: wrappingModel } = await getWrappingModel();

        if (!wrappingModel) return;

        modal({
            content: (
                <WrappingView
                    viewModel={
                        isWrappingCardOnly
                            ? { ...wrappingModel, wrappingQuantity: wrappingModel?.basketLineQuantity }
                            : wrappingModel
                    }
                    modal={modal}
                    rebindBasket={rebindBasket}
                    isWrappingCardOnly={isWrappingCardOnly}
                />
            ),
            open: true
        });
    };

    const handleOpenWrappingModalWithConfirmation = async () => {
        const { default: confirmationModal } = await import('modules/modal/confirmationModal');

        const modal = confirmationModal();

        const onConfirm = handleOpenWrappingModal;

        const onDeny = async () => {
            const { willAffectSamplesConfirm } = await import('views/basket/samples');

            try {
                if (productId && !(await willAffectSamplesConfirm({ productId, wrappingQuantity: 0 }))) {
                    return;
                }
                await deleteAllWrapping({ params: { productId } });

                GA4Tracking({
                    context: 'basket',
                    category: 'ecommerce',
                    eventName: 'toggle_gift_wrapping',
                    eventParams: [{ param_name: 'toggle', param_value: false }]
                });
            } catch (error) {
                console.error(error);
            }
        };

        modal({
            open: true,
            header: t(Heading),
            content: <ConfirmModalContent message={t(Message)} />,
            confirmText: t(ConfirmButton),
            denyText: t(Delete),
            onConfirm,
            onDeny,
            denyOnClose: false
        });
    };

    return { handleOpenWrappingModal, handleOpenWrappingModalWithConfirmation };
}
